import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PayrollService } from '@/services/payroll.service';
import { PayrollRecord, PayPeriod, PayrollStatus } from '@/models/payroll.model';
import { HRService } from '@/services/hr.service';
import { EarningsCalculatorService } from '@/services/earnings-calculator.service';

export const PayrollRecordForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;
  const employeeId = searchParams.get('employeeId') || '';

  const [formData, setFormData] = useState<Partial<PayrollRecord>>({
    employeeId,
    payPeriod: PayPeriod.biWeekly,
    periodStart: new Date(),
    periodEnd: new Date(),
    payDate: new Date(),
    status: PayrollStatus.draft,
    grossPay: 0,
    federalTax: 0,
    stateTax: 0,
    localTax: 0,
    socialSecurity: 0,
    medicare: 0,
    otherDeductions: 0,
    totalDeductions: 0,
    netPay: 0,
    benefitsCost: 0,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: record, isLoading } = useQuery({
    queryKey: ['payrollRecord', id],
    queryFn: () => id ? PayrollService.getPayrollRecordById(id) : null,
    enabled: isEditing && !!id,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => HRService.getAllEmployees(),
  });

  const selectedEmployee = employees.find(e => e.id === formData.employeeId);

  React.useEffect(() => {
    if (record) {
      setFormData(record);
    }
  }, [record]);

  // Determine if employee is hourly or salaried
  const isHourly = selectedEmployee 
    ? (selectedEmployee.hourlyRate != null && selectedEmployee.hourlyRate > 0) 
    : (formData.hourlyRate != null && formData.hourlyRate > 0);

  React.useEffect(() => {
    // Calculate gross pay using earnings calculator
    try {
      const employeeData = {
        hourlyRate: formData.hourlyRate ?? selectedEmployee?.hourlyRate,
        salary: formData.salaryAmount ?? selectedEmployee?.salary,
        isHourly: isHourly,
      };

      const hoursWorked = {
        regular: formData.regularHours,
        overtime: formData.overtimeHours,
      };

      const additionalEarnings = {
        bonuses: formData.bonuses || 0,
        commissions: formData.commissions || 0,
        other: formData.otherEarnings || 0,
      };

      // Only calculate if we have the necessary data
      if (isHourly && employeeData.hourlyRate && employeeData.hourlyRate > 0) {
        // Hourly employee - calculate from hours and rate
        const result = EarningsCalculatorService.calculateEarnings({
          isHourly: true,
          hourlyRate: employeeData.hourlyRate,
          regularHours: hoursWorked.regular || 0,
          overtimeHours: hoursWorked.overtime || 0,
          payPeriod: formData.payPeriod || PayPeriod.biWeekly,
          bonuses: additionalEarnings.bonuses,
          commissions: additionalEarnings.commissions,
          otherEarnings: additionalEarnings.other,
        });
        
        setFormData(prev => ({
          ...prev,
          grossPay: result.totalEarnings,
        }));
      } else if (!isHourly && employeeData.salary && employeeData.salary > 0) {
        // Salaried employee - calculate from annual salary
        const result = EarningsCalculatorService.calculateEarnings({
          isHourly: false,
          annualSalary: employeeData.salary,
          payPeriod: formData.payPeriod || PayPeriod.biWeekly,
          bonuses: additionalEarnings.bonuses,
          commissions: additionalEarnings.commissions,
          otherEarnings: additionalEarnings.other,
        });
        
        setFormData(prev => ({
          ...prev,
          grossPay: result.totalEarnings,
        }));
      } else {
        // No rate/salary set, just use bonuses/commissions/other
        const gross = (formData.bonuses || 0) + (formData.commissions || 0) + (formData.otherEarnings || 0);
        setFormData(prev => ({
          ...prev,
          grossPay: gross,
        }));
      }
    } catch (error) {
      console.error('Error calculating earnings:', error);
      // Fallback to manual calculation
      const gross = (formData.bonuses || 0) + (formData.commissions || 0) + (formData.otherEarnings || 0);
      setFormData(prev => ({
        ...prev,
        grossPay: gross,
      }));
    }
  }, [
    formData.regularHours,
    formData.overtimeHours,
    formData.hourlyRate,
    formData.salaryAmount,
    formData.payPeriod,
    formData.bonuses,
    formData.commissions,
    formData.otherEarnings,
    selectedEmployee?.hourlyRate,
    selectedEmployee?.salary,
    isHourly,
  ]);

  React.useEffect(() => {
    // Calculate deductions and net pay
    const deductions = (formData.federalTax || 0) + (formData.stateTax || 0) + (formData.localTax || 0) + 
                       (formData.socialSecurity || 0) + (formData.medicare || 0) + (formData.otherDeductions || 0);
    const net = (formData.grossPay || 0) - deductions;
    
    setFormData(prev => ({
      ...prev,
      totalDeductions: deductions,
      netPay: net,
    }));
  }, [formData.grossPay, formData.federalTax, formData.stateTax, formData.localTax, 
      formData.socialSecurity, formData.medicare, formData.otherDeductions]);

  const mutation = useMutation({
    mutationFn: async (data: Partial<PayrollRecord>) => {
      if (isEditing && id) {
        await PayrollService.updatePayrollRecord(id, data);
      } else {
        await PayrollService.createPayrollRecord(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payrollRecords'] });
      navigate('/payroll');
    },
    onError: (error: any) => {
      setErrorMessage(error?.message || 'Failed to save payroll record');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.employeeId || !formData.periodStart || !formData.periodEnd || !formData.payDate) {
      setErrorMessage('Employee, Period Dates, and Pay Date are required');
      return;
    }

    if (selectedEmployee) {
      formData.employeeName = selectedEmployee.fullName;
    }

    mutation.mutate(formData);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Payroll Record' : 'New Payroll Record'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Employee *</label>
            <select
              required
              value={formData.employeeId}
              onChange={(e) => {
                const employeeId = e.target.value;
                const emp = employees.find(emp => emp.id === employeeId);
                setFormData({
                  ...formData,
                  employeeId,
                  employeeName: emp?.fullName || '',
                  hourlyRate: emp?.hourlyRate,
                  salaryAmount: emp?.salary,
                  // Reset hours when employee changes
                  regularHours: undefined,
                  overtimeHours: undefined,
                });
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.fullName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Pay Period *</label>
            <select
              required
              value={formData.payPeriod}
              onChange={(e) => setFormData({ ...formData, payPeriod: e.target.value as PayPeriod })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.values(PayPeriod).map(period => (
                <option key={period} value={period}>{period.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Period Start *</label>
            <input
              type="date"
              required
              value={formData.periodStart ? new Date(formData.periodStart).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, periodStart: new Date(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Period End *</label>
            <input
              type="date"
              required
              value={formData.periodEnd ? new Date(formData.periodEnd).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, periodEnd: new Date(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Pay Date *</label>
            <input
              type="date"
              required
              value={formData.payDate ? new Date(formData.payDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, payDate: new Date(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status *</label>
            <select
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as PayrollStatus })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.values(PayrollStatus).map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Earnings</h3>
          
          {/* Employee Type Indicator */}
          {selectedEmployee && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-900">
                <strong>Employee Type:</strong> {isHourly ? 'Hourly' : 'Salaried'}
                {isHourly && selectedEmployee.hourlyRate && (
                  <span className="ml-2">• Rate: ${selectedEmployee.hourlyRate.toFixed(2)}/hr</span>
                )}
                {!isHourly && selectedEmployee.salary && (
                  <span className="ml-2">• Annual Salary: ${selectedEmployee.salary.toLocaleString()}</span>
                )}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {isHourly ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Regular Hours *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.regularHours || ''}
                    onChange={(e) => setFormData({ ...formData, regularHours: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 80"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Standard: {EarningsCalculatorService.getStandardHoursPerPeriod(formData.payPeriod || PayPeriod.biWeekly).toFixed(2)} hrs per {formData.payPeriod?.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Overtime Hours</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.overtimeHours || ''}
                    onChange={(e) => setFormData({ ...formData, overtimeHours: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 5"
                  />
                  <p className="mt-1 text-xs text-gray-500">Paid at 1.5x rate (time-and-a-half)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Hourly Rate *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourlyRate || selectedEmployee?.hourlyRate || ''}
                    onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 25.00"
                  />
                  {selectedEmployee?.hourlyRate && formData.hourlyRate !== selectedEmployee.hourlyRate && (
                    <p className="mt-1 text-xs text-yellow-600">
                      Employee default: ${selectedEmployee.hourlyRate.toFixed(2)}/hr
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Annual Salary *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.salaryAmount || selectedEmployee?.salary || ''}
                    onChange={(e) => setFormData({ ...formData, salaryAmount: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 50000"
                  />
                  {selectedEmployee?.salary && formData.salaryAmount !== selectedEmployee.salary && (
                    <p className="mt-1 text-xs text-yellow-600">
                      Employee default: ${selectedEmployee.salary.toLocaleString()}/year
                    </p>
                  )}
                  {formData.salaryAmount && formData.payPeriod && (
                    <p className="mt-1 text-xs text-gray-500">
                      Per {formData.payPeriod.replace('_', ' ')}: ${(formData.salaryAmount / EarningsCalculatorService.getPayPeriodsPerYear(formData.payPeriod)).toFixed(2)}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-sm text-gray-600">
                      <strong>Note:</strong> Salaried employees are paid a fixed amount per pay period based on their annual salary.
                      Regular hours and overtime are typically not applicable for salaried employees.
                    </p>
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Bonuses</label>
              <input
                type="number"
                step="0.01"
                value={formData.bonuses || 0}
                onChange={(e) => setFormData({ ...formData, bonuses: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Commissions</label>
              <input
                type="number"
                step="0.01"
                value={formData.commissions || 0}
                onChange={(e) => setFormData({ ...formData, commissions: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Other Earnings</label>
              <input
                type="number"
                step="0.01"
                value={formData.otherEarnings || 0}
                onChange={(e) => setFormData({ ...formData, otherEarnings: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Gross Pay (Auto-calculated)</label>
              <input
                type="number"
                step="0.01"
                value={formData.grossPay || 0}
                disabled
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 font-semibold"
              />
              {(formData.grossPay || 0) > 0 && (
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  {(() => {
                    try {
                      if (isHourly && formData.hourlyRate && formData.regularHours) {
                        const regularPay = (formData.regularHours || 0) * (formData.hourlyRate || 0);
                        const overtimePay = (formData.overtimeHours || 0) * (formData.hourlyRate || 0) * 1.5;
                        const additional = (formData.bonuses || 0) + (formData.commissions || 0) + (formData.otherEarnings || 0);
                        return (
                          <>
                            {regularPay > 0 && <div>Regular: ${regularPay.toFixed(2)}</div>}
                            {overtimePay > 0 && <div>Overtime: ${overtimePay.toFixed(2)}</div>}
                            {additional > 0 && <div>Additional: ${additional.toFixed(2)}</div>}
                          </>
                        );
                      } else if (!isHourly && formData.salaryAmount && formData.payPeriod) {
                        const salaryPay = formData.salaryAmount / EarningsCalculatorService.getPayPeriodsPerYear(formData.payPeriod);
                        const additional = (formData.bonuses || 0) + (formData.commissions || 0) + (formData.otherEarnings || 0);
                        return (
                          <>
                            <div>Salary: ${salaryPay.toFixed(2)}</div>
                            {additional > 0 && <div>Additional: ${additional.toFixed(2)}</div>}
                          </>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Deductions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Federal Tax</label>
              <input
                type="number"
                step="0.01"
                value={formData.federalTax || 0}
                onChange={(e) => setFormData({ ...formData, federalTax: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State Tax</label>
              <input
                type="number"
                step="0.01"
                value={formData.stateTax || 0}
                onChange={(e) => setFormData({ ...formData, stateTax: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Local Tax</label>
              <input
                type="number"
                step="0.01"
                value={formData.localTax || 0}
                onChange={(e) => setFormData({ ...formData, localTax: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Social Security</label>
              <input
                type="number"
                step="0.01"
                value={formData.socialSecurity || 0}
                onChange={(e) => setFormData({ ...formData, socialSecurity: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Medicare</label>
              <input
                type="number"
                step="0.01"
                value={formData.medicare || 0}
                onChange={(e) => setFormData({ ...formData, medicare: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Other Deductions</label>
              <input
                type="number"
                step="0.01"
                value={formData.otherDeductions || 0}
                onChange={(e) => setFormData({ ...formData, otherDeductions: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Deductions</label>
              <input
                type="number"
                step="0.01"
                value={formData.totalDeductions || 0}
                disabled
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Benefits Cost</label>
              <input
                type="number"
                step="0.01"
                value={formData.benefitsCost || 0}
                onChange={(e) => setFormData({ ...formData, benefitsCost: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-blue-900">Net Pay</span>
              <span className="text-2xl font-bold text-blue-900">
                ${(formData.netPay || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/payroll')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

